<template>
    <v-container fluid class="pa-6">
      <!-- Cabecera -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <h1 class="headline font-weight-bold mb-1">Frecuencias de Mantenimiento</h1>
          <p class="subtitle-1 grey--text text--darken-1">Configura los intervalos de tiempo para las tareas</p>
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center justify-md-end">
          <v-btn
            color="primary"
            large
            depressed
            class="rounded-lg"
            @click="openDialog()"
          >
            <v-icon left>mdi-plus</v-icon>
            Nueva Frecuencia
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- Tabla de Frecuencias -->
      <v-card outlined class="rounded-xl mt-2">
        <v-card-title class="pa-4">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Buscar frecuencia..."
            outlined
            dense
            hide-details
            style="max-width: 400px"
            clearable
          ></v-text-field>
        </v-card-title>
  
        <v-data-table
          :headers="headers"
          :items="frecuencias"
          :search="search"
          class="bg-transparent"
        >
          <!-- Formato para Días -->
          <template v-slot:item.dias="{ item }">
            <v-chip outlined small color="blue-grey darken-2">
              Cada {{ item.dias }} días
            </v-chip>
          </template>
  
          <!-- Estado -->
          <template v-slot:item.active="{ item }">
            <v-chip
              :color="item.active ? 'success' : 'error'"
              small
              dark
            >
              {{ item.active ? 'Activa' : 'Anulada' }}
            </v-chip>
          </template>
  
          <!-- Acciones -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-btn
                icon
                small
                color="primary"
                class="mr-1"
                @click="openDialog(item)"
                title="Editar"
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              
              <v-btn
                icon
                small
                :color="item.active ? 'error' : 'success'"
                @click="toggleFrecuencia(item)"
                :title="item.active ? 'Anular frecuencia' : 'Activar frecuencia'"
              >
                <v-icon>{{ item.active ? 'mdi-minus-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- Modal Crear/Editar -->
      <v-dialog v-model="dialog" max-width="500px" persistent>
        <v-card class="rounded-xl pa-2">
          <v-card-title class="headline font-weight-bold pa-4">
            {{ isEditing ? 'Editar Frecuencia' : 'Nueva Frecuencia' }}
          </v-card-title>
  
          <v-card-text>
            <v-form ref="formRef" v-model="isFormValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.descripcion"
                    label="Descripción"
                    outlined
                    placeholder="Ej: Mensual, Semestral..."
                    :rules="[v => !!v || 'La descripción es obligatoria']"
                    required
                  ></v-text-field>
                </v-col>
  
                <v-col cols="12">
                  <v-text-field
                    v-model.number="editedItem.dias"
                    label="Intervalo en días"
                    type="number"
                    outlined
                    placeholder="30"
                    suffix="días"
                    :rules="[
                      v => !!v || 'El número de días es obligatorio',
                      v => v > 0 || 'Debe ser mayor a 0'
                    ]"
                    required
                  ></v-text-field>
                </v-col>
  
                <v-col cols="12">
                  <v-switch
                    v-model="editedItem.active"
                    :label="editedItem.active ? 'Frecuencia Activa' : 'Frecuencia Inactiva'"
                    color="success"
                    inset
                    hide-details
                  ></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
  
          <v-divider class="mx-4"></v-divider>
  
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeDialog">Cancelar</v-btn>
            <v-btn
              color="primary"
              depressed
              width="120"
              :loading="isSaving"
              :disabled="!isFormValid"
              @click="saveFrecuencia"
            >
              {{ isEditing ? 'Actualizar' : 'Guardar' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Modal de error por frecuencia duplicada -->
      <v-dialog v-model="duplicateDialog" max-width="420px">
        <v-card class="pa-4 rounded-lg">
          <v-card-title class="headline font-weight-bold">
            Frecuencia duplicada
          </v-card-title>
  
          <v-card-text>
            Ya existe una frecuencia con esta <strong>descripción</strong> y este <strong>intervalo de días</strong>.
            Por favor, introduce una combinación diferente.
          </v-card-text>
  
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed @click="duplicateDialog = false">
              Entendido
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Snackbar -->
      <v-snackbar 
        v-model="notify.show" 
        :color="notify.color" 
        :timeout="notify.timeout"
        top
      >
        <v-icon left dark>{{ notify.icon }}</v-icon>
        {{ notify.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="notify.show = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
  import axios from 'axios'
  
  export default {
    name: 'FrecuenciasMantenimiento',
    data() {
      return {
        // Notificaciones
        notify: {
          show: false,
          text: '',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000
        },
        // UI
        search: '',
        dialog: false,
        isSaving: false,
        isFormValid: false,
        isEditing: false,
        duplicateDialog: false,
        // Tabla (V2 usa text/value)
        headers: [
          { text: 'ID', value: 'id', align: 'start', width: '80px' },
          { text: 'Descripción', value: 'descripcion', align: 'start' },
          { text: 'Intervalo', value: 'dias', align: 'start' },
          { text: 'Estado', value: 'active', align: 'center', width: '150px' },
          { text: 'Acciones', value: 'actions', align: 'end', sortable: false },
        ],
        frecuencias: [],
        // Modelos
        editedItem: {
          id: null,
          descripcion: '',
          dias: null,
          active: true
        },
        defaultItem: {
          id: null,
          descripcion: '',
          dias: null,
          active: true
        }
      }
    },
    computed: {
      // Obtenemos el ID de la empresa del localStorage
      idEmpresa() {
        return localStorage.getItem("clienteSeleccionado");
      }
    },
    mounted() {
      this.loadFrecuencias();
    },
    methods: {
      showNotify(text, color = 'success') {
        this.notify.text = text;
        this.notify.color = color;
        if (color === 'error') this.notify.icon = 'mdi-alert-circle';
        else if (color === 'info') this.notify.icon = 'mdi-information';
        else this.notify.icon = 'mdi-check-circle';
        this.notify.show = true;
      },
      async loadFrecuencias() {
        if (!this.idEmpresa) return;
  
        try {
          const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/frecuencias`, {
            params: { id_empresa: this.idEmpresa } // Enviado como query param
          });
          this.frecuencias = res.data.frecuencias || res.data;
        } catch (error) {
          this.showNotify('Error al cargar las frecuencias', 'error');
        }
      },
      existsDuplicateFrecuencia(descripcion, dias, id = null) {
        const descLower = descripcion.trim().toLowerCase();
        return this.frecuencias.some(f =>
          f.descripcion.trim().toLowerCase() === descLower &&
          Number(f.dias) === Number(dias) &&
          f.id !== id
        );
      },
      openDialog(item = null) {
        if (item) {
          this.isEditing = true;
          this.editedItem = JSON.parse(JSON.stringify(item));
        } else {
          this.isEditing = false;
          this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
        }
        this.dialog = true;
      },
      closeDialog() {
        this.dialog = false;
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      },
      async saveFrecuencia() {
        if (!this.isFormValid) return;
        this.isSaving = true;
  
        try {
          if (this.existsDuplicateFrecuencia(this.editedItem.descripcion, this.editedItem.dias, this.editedItem.id)) {
            this.duplicateDialog = true;
            this.isSaving = false;
            return;
          }
  
          const payload = {
            descripcion: this.editedItem.descripcion,
            dias: this.editedItem.dias,
            active: this.editedItem.active,
            id_empresa: this.idEmpresa // Incluimos el ID de empresa en el body
          };
  
          if (this.isEditing) {
            await axios.put(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/frecuencias/${this.editedItem.id}`, payload);
            this.showNotify('Frecuencia actualizada con éxito', 'success');
          } else {
            await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/frecuencias`, payload);
            this.showNotify('Nueva frecuencia creada', 'success');
          }
  
          await this.loadFrecuencias();
          this.closeDialog();
        } catch (error) {
          this.showNotify('Error al guardar la frecuencia', 'error');
        } finally {
          this.isSaving = false;
        }
      },
      async toggleFrecuencia(item) {
        try {
          const nuevoEstado = !item.active;
          await axios.patch(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/frecuencias/${item.id}/estado`, {
            active: nuevoEstado,
            id_empresa: this.idEmpresa // Necesario para la validación de seguridad en el backend
          });
          this.showNotify(nuevoEstado ? 'Frecuencia activada' : 'Frecuencia anulada', 'info');
          await this.loadFrecuencias();
        } catch (error) {
          this.showNotify('Error al cambiar el estado', 'error');
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .rounded-xl {
    border-radius: 24px !important;
  }
  .rounded-lg {
    border-radius: 12px !important;
  }
  
  ::v-deep .v-data-table-header th {
    font-weight: bold !important;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
    color: #757575 !important;
  }
  
  ::v-deep .v-data-table__wrapper tr:hover {
    background-color: rgba(0, 0, 0, 0.02) !important;
  }
  </style>